import React, { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import { notification } from 'antd';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';

type NotificationArgs = Parameters<typeof notification.error>[0];

const defaultIcons = {
  success: <CheckCircleFilled aria-hidden="true" style={{ color: '#52c41a' }} />,
  info: <InfoCircleFilled aria-hidden="true" style={{ color: '#1677ff' }} />,
  warning: <ExclamationCircleFilled aria-hidden="true" style={{ color: '#faad14' }} />,
  error: <CloseCircleFilled aria-hidden="true" style={{ color: '#ff4d4f' }} />
};

interface NotificationContextValue {
  successNotification: (args: NotificationArgs) => void;
  infoNotification: (args: NotificationArgs) => void;
  warningNotification: (args: NotificationArgs) => void;
  errorNotification: (args: NotificationArgs) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const successNotification = useCallback((args: NotificationArgs) => {
    notification.success({ icon: defaultIcons.success, ...args });
  }, []);

  const infoNotification = useCallback((args: NotificationArgs) => {
    notification.info({ icon: defaultIcons.info, ...args });
  }, []);

  const warningNotification = useCallback((args: NotificationArgs) => {
    notification.warning({ icon: defaultIcons.warning, ...args });
  }, []);

  const errorNotification = useCallback((args: NotificationArgs) => {
    notification.error({ icon: defaultIcons.error, ...args });
  }, []);

  const notificationHandlers = useMemo(
    () => ({
      successNotification: successNotification,
      infoNotification: infoNotification,
      warningNotification: warningNotification,
      errorNotification: errorNotification
    }),
    [successNotification, infoNotification, warningNotification, errorNotification]
  );

  return <NotificationContext.Provider value={notificationHandlers}>{children}</NotificationContext.Provider>;
}

export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
